import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { format } from 'date-fns';
import { ko, es, enUS } from 'date-fns/locale';
import { useLanguage } from '@/contexts/AppContext';
import { profileTranslations } from './profileTranslations';

interface BirthdayConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tempBirthday: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function BirthdayConfirmDialog({
  open,
  onOpenChange,
  tempBirthday,
  onConfirm,
  onCancel,
}: BirthdayConfirmDialogProps) {
  // #522 — 번역 lookup 은 15 locale 전수 appLocale 사용. date-fns locale 은 기존 language 파생 유지.
  const { language, locale: appLocale } = useLanguage();
  const t = profileTranslations[appLocale];
  const locale = language === 'ko' ? ko : language === 'es' ? es : enUS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(90vw,28rem)] bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-[min(5vw,1.25rem)] text-foreground">
            {t.birthdayConfirmTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t.birthdayConfirmMessage}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-[min(4vw,1rem)] pt-4">
          {/* Warning Message */}
          <div className="p-4 rounded-[8px] bg-warning/10 border border-warning/30">
            <p className="text-[min(3.5vw,0.875rem)] text-foreground whitespace-pre-line">
              {t.birthdayConfirmMessage}
            </p>
          </div>

          {/* Selected Date Display */}
          {tempBirthday && (
            <div className="p-3 rounded-[5px] bg-muted">
              <p className="text-[min(3vw,0.75rem)] text-muted-foreground mb-1">
                {t.selectedDate}:
              </p>
              <p className="text-[min(4vw,1rem)] font-bold text-primary">
                {format(new Date(tempBirthday), 'PPP', { locale })}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={onCancel}
              className="flex-1 bg-muted text-foreground h-[min(12vw,3rem)] rounded-[5px] font-medium border-2 border-border"
            >
              {t.cancel}
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-primary text-white h-[min(12vw,3rem)] rounded-[5px] font-medium shadow-md shadow-primary/20"
            >
              {t.confirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveAndExit: () => void;
  onDiscardChanges: () => void;
}

export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onSaveAndExit,
  onDiscardChanges,
}: UnsavedChangesDialogProps) {
  const { locale } = useLanguage();
  const t = profileTranslations[locale];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(90vw,28rem)] bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-[min(5vw,1.25rem)] text-foreground">
            {t.unsavedChangesTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t.unsavedChangesMessage}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-[min(4vw,1rem)] pt-4">
          {/* Warning Message */}
          <div className="p-4 rounded-[8px] bg-warning/10 border border-warning/30">
            <p className="text-[min(3.5vw,0.875rem)] text-foreground whitespace-pre-line">
              {t.unsavedChangesMessage}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={onDiscardChanges}
              className="flex-1 bg-muted text-foreground h-[min(12vw,3rem)] rounded-[5px] font-medium border-2 border-border"
            >
              {t.discardChanges}
            </Button>
            <Button
              onClick={onSaveAndExit}
              className="flex-1 bg-primary text-white h-[min(12vw,3rem)] rounded-[5px] font-medium shadow-md shadow-primary/20"
            >
              {t.saveAndExit}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
